import { MutableRefObject, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import { v4 as uuidv4 } from "uuid";
import logo from "@/assets/logo.svg";

import { IStep, useChatData, useChatInteract } from "@chainlit/react-client";

import { persistentCommandState } from "state/chat";

import Input, { InputMethods } from "./Input";
import SubmitButton from "./SubmitButton";
import { cn } from "@/lib/utils";

interface Props {
  autoScrollRef: MutableRefObject<boolean>;
  isWelcome?: boolean;
}

export default function MessageComposer({ autoScrollRef, isWelcome }: Props) {
  const inputRef = useRef<InputMethods>(null);
  const [value, setValue] = useState("");
  const [selectedCommand, setSelectedCommand] = useRecoilState(
    persistentCommandState
  );
  const user = {};
  const { sendMessage } = useChatInteract();
  const { disabled: _disabled } = useChatData();

  const onSubmit = useCallback(
    async (msg: string, selectedCommand?: string) => {
      const message: IStep = {
        threadId: "",
        command: selectedCommand,
        id: uuidv4(),
        name: "User",
        type: "user_message",
        output: msg,
        createdAt: new Date().toISOString(),
        metadata: { location: window.location.href },
      };

      if (autoScrollRef) {
        autoScrollRef.current = true;
      }
      sendMessage(message);
    },
    [user, sendMessage]
  );

  const submit = useCallback(() => {
    if (value === "" && !selectedCommand) {
      return;
    }
    onSubmit(value, selectedCommand?.id);
    inputRef.current?.reset();
  }, [value, setValue, selectedCommand, onSubmit]);

  return (
    <div
      id="message-composer"
      className={cn(
        "bg-accent dark:bg-card rounded-3xl p-3 px-4 w-full min-h-24 flex flex-col message-composer",
        {
          "max-w-[580px] mx-auto is-welcome-view": isWelcome,
        }
      )}
    >
      <img src={logo} alt="Digitalya" className="w-6 h-6 mr-[8px]" />
      <Input
        ref={inputRef}
        id="chat-input"
        autoFocus
        selectedCommand={selectedCommand}
        setSelectedCommand={setSelectedCommand}
        onChange={setValue}
        onEnter={submit}
        placeholder="Type your question here"
      />
      <div className="flex items-center justify-between mt-auto">
        <div className="flex items-center gap-1">
          <SubmitButton
            onSubmit={submit}
            disabled={!value.trim() && !selectedCommand}
          />
        </div>
      </div>
    </div>
  );
}
